<script setup lang="ts">
import { computed, ref, watch, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes.ts";
import DataViewProducts from "./DataViewProducts.vue";
import ProfileCarousel from "./ProfileCarousel.vue";
import { isMobileView } from "../../utils/GeneralUtils.ts";

const { t } = useI18n();

const props = defineProps({
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    availableProductsByModel: {
        type: Object as PropType<Record<string, TranslatedProduct[]>>,
        required: true,
    },
});

const emit = defineEmits(["update"]);

// ─── Layout

const layout = ref<"list" | "grid">("grid");
const layoutOptions = ref(["list", "grid"]);
const isDesktop = computed(() => !isMobileView());

watch(
    isDesktop,
    (val) => {
        if (!val) layout.value = "grid";
    },
    { immediate: true },
);

// ─── Filters

const modelFilterValue = ref();
const productFilterValue = ref();

const modelOptions = computed(() =>
    Object.keys(props.availableProductsByModel).map((modelName) => ({ name: modelName })),
);

const productOptions = computed(() =>
    Object.keys(props.availableProductsByModel).map((modelName) => ({
        label: modelName,
        items: props.availableProductsByModel[modelName].map((product) => ({
            name: product.productName,
            id: product.productNo,
            model: modelName,
        })),
    })),
);

// ─── Filtered products

const filteredAvailableProducts = ref<Record<string, TranslatedProduct[]>>({});

function filterProducts(
    modelFilterNames: string[],
    productFilterIds: string[],
    products: Record<string, TranslatedProduct[]>,
): Record<string, TranslatedProduct[]> {
    const hasModelFilter = modelFilterNames.length > 0;
    const hasProductFilter = productFilterIds.length > 0;

    if (!hasModelFilter && !hasProductFilter) return products;

    let result: Record<string, TranslatedProduct[]> = {};

    if (hasModelFilter) {
        result = Object.keys(products)
            .filter((key) => modelFilterNames.includes(key))
            .reduce(
                (acc, key) => {
                    acc[key] = products[key];
                    return acc;
                },
                {} as Record<string, TranslatedProduct[]>,
            );
    } else {
        result = { ...products };
    }

    if (hasProductFilter) {
        const byProduct: Record<string, TranslatedProduct[]> = {};
        Object.keys(result).forEach((modelName) => {
            const filtered = result[modelName].filter((p) => productFilterIds.includes(p.productNo));
            if (filtered.length > 0) byProduct[modelName] = filtered;
        });
        return byProduct;
    }

    return result;
}

function applyFilters() {
    const modelFilterNames = modelFilterValue.value?.map((item: { name: string }) => item.name) ?? [];
    const productFilterIds = productFilterValue.value?.map((item: { id: string }) => item.id) ?? [];
    filteredAvailableProducts.value = filterProducts(
        modelFilterNames,
        productFilterIds,
        props.availableProductsByModel,
    );
}

watch(() => props.availableProductsByModel, applyFilters, { immediate: true });

watch(
    () => modelFilterValue.value,
    (newValue) => {
        if (newValue && newValue.length > 0) productFilterValue.value = null;
        applyFilters();
    },
);

watch(
    () => productFilterValue.value,
    (newValue) => {
        if (newValue && newValue.length > 0) modelFilterValue.value = null;
        applyFilters();
    },
);

// ─── Events

function handleUpdate(needsUpdate: boolean, product: string) {
    emit("update", needsUpdate, product);
}

// ─── Carousel

const carouselEmpty = ref(false);

const handleEmptyCarousel = (isEmpty: boolean) => {
    carouselEmpty.value = isEmpty;
};

function getTranslatedProducts() {
    return Object.values(filteredAvailableProducts.value).flat();
}
</script>

<template>
    <div class="pt-4">
        <!-- Filters -->
        <div class="profile-tab-toolbar mb-6">
            <div class="profile-tab-filters">
                <MultiSelect
                    id="buy-profiles-tab_input_product-filter"
                    data-cy="buy-product-filter_selector"
                    v-model="productFilterValue"
                    display="chip"
                    filter
                    :options="productOptions"
                    option-label="name"
                    option-group-label="label"
                    option-group-children="items"
                    :placeholder="t('profile-overview.filterByProduct')"
                    :max-selected-labels="2"
                    show-clear
                />
                <MultiSelect
                    id="buy-profiles-tab_input_model-filter"
                    data-cy="buy-model-filter_selector"
                    v-model="modelFilterValue"
                    display="chip"
                    filter
                    :options="modelOptions"
                    option-label="name"
                    :placeholder="t('profile-overview.filterByModel')"
                    :max-selected-labels="3"
                    show-clear
                />
            </div>
            <div class="profile-tab-toolbar-right">
                <Message severity="warn" icon="pi pi-info-circle" class="ai-safety-message">
                    {{ t("profile-overview.aiSafetyNotice") }}
                </Message>
                <SelectButton
                    v-if="isDesktop"
                    v-model="layout"
                    :options="layoutOptions"
                    :allow-empty="false"
                    :aria-label="t('accessibility.selectors.viewMode')"
                    id="buy-profiles-tab_select-button_grid-options"
                >
                    <template #option="{ option }">
                        <i :class="[option === 'list' ? 'pi pi-bars' : 'pi pi-th-large']" />
                    </template>
                </SelectButton>
            </div>
        </div>

        <!-- No products match filter -->
        <Message
            v-if="Object.keys(filteredAvailableProducts).length === 0"
            severity="info"
        >
            {{ t("profile-overview.noProductsFromModelMessage") }}
        </Message>

        <DataViewProducts
            v-else
            :trainer="trainer"
            :products="filteredAvailableProducts"
            :layout="layout"
            @update="handleUpdate"
        />

        <!-- Cross-sell carousel -->
        <div
            v-if="Object.keys(filteredAvailableProducts).length > 0"
            class="bg-white p-6 mb-8 mt-8"
        >
            <h2 v-if="!carouselEmpty" class="text-2xl font-bold mt-2 mb-4">
                {{ t("profile-overview.cross-sell.heading") }}
            </h2>
            <ProfileCarousel
                :key="JSON.stringify(Object.keys(filteredAvailableProducts))"
                :trainer="trainer"
                :profiles="getTranslatedProducts()"
                @update="handleUpdate"
                @empty-carousel="handleEmptyCarousel"
            />
        </div>
    </div>
</template>

<style scoped>
.profile-tab-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.75rem;
}

.profile-tab-filters {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    flex: 0 1 auto;
}

.profile-tab-toolbar-right {
    display: flex;
    flex: 1 1 24rem;
    min-width: 18rem;
    align-items: flex-start;
    gap: 0.5rem;
}

.ai-safety-message {
    margin: 0;
    flex: 1 1 auto;
    border: 1px solid var(--p-primary-500);
    background: var(--p-primary-50);
    color: var(--p-primary-700);
    outline: none;
    box-shadow: none;
}

.ai-safety-message.p-message-warn {
    border-color: var(--p-secondary-500) !important;
    background: var(--p-secondary-50) !important;
    outline: none !important;
    box-shadow: none !important;
}

.ai-safety-message :deep(.p-message-content) {
    align-items: flex-start;
    padding: 0.45rem 0.65rem;
}

.ai-safety-message :deep(.p-message-icon) {
    color: var(--p-secondary-600);
    margin-top: 0.1rem;
}

.ai-safety-message :deep(.p-message-text) {
    color: var(--p-secondary-700);
    font-size: 0.8rem;
    line-height: 1.2rem;
}

.profile-tab-toolbar-right :deep(.p-selectbutton) {
    margin-left: auto;
}

@media screen and (max-width: 768px) {
    .profile-tab-filters {
        width: 100%;
    }

    .profile-tab-toolbar-right {
        width: 100%;
        flex-direction: column;
        min-width: 0;
    }

    .ai-safety-message {
        width: 100%;
    }
}
</style>
